'use client'

import {useEffect, useId, useRef, useState, type HTMLAttributes, type PointerEvent} from 'react'
import styles from './VisibleScrollArea.module.css'

/** A persistent scrollbar for compact lists, independent of OS overlay-scrollbar settings. */
export function VisibleScrollArea({children, className, ...props}: HTMLAttributes<HTMLDivElement>) {
    const id = useId()
    const viewport = useRef<HTMLDivElement>(null)
    const content = useRef<HTMLDivElement>(null)
    const rail = useRef<HTMLDivElement>(null)
    const dragOffset = useRef<number | null>(null)
    const [metrics, setMetrics] = useState({height: 0, total: 0, top: 0})
    const max = Math.max(0, metrics.total - metrics.height)
    const trackHeight = Math.max(0, metrics.height - 8)
    const thumbHeight = Math.min(trackHeight, Math.max(28, trackHeight * metrics.height / (metrics.total || 1)))
    const travel = trackHeight - thumbHeight
    const thumbTop = max ? metrics.top / max * travel : 0

    useEffect(() => {
        const element = viewport.current
        if (!element) return
        const update = () => setMetrics({height: element.clientHeight, total: element.scrollHeight, top: element.scrollTop})
        update()
        element.addEventListener('scroll', update, {passive: true})
        const observer = new ResizeObserver(update)
        observer.observe(element)
        if (content.current) observer.observe(content.current)
        return () => { element.removeEventListener('scroll', update); observer.disconnect() }
    }, [])

    const move = (event: PointerEvent<HTMLDivElement>) => {
        if (dragOffset.current === null || !rail.current || !viewport.current || travel <= 0) return
        const top = event.clientY - rail.current.getBoundingClientRect().top - dragOffset.current
        viewport.current.scrollTop = Math.max(0, Math.min(1, top / travel)) * max
    }

    return <div className={styles.root}>
        <div {...props} id={props.id ?? id} ref={viewport} className={styles.viewport} style={{paddingRight: max > 0 ? 14 : undefined, ...props.style}}>
            <div ref={content} className={className}>{children}</div>
        </div>
        {max > 0 ? <div ref={rail} className={styles.rail}
            role="scrollbar" tabIndex={0} aria-label={props['aria-label']}
            aria-controls={props.id ?? id} aria-orientation="vertical"
            aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.round(metrics.top)}
            onPointerDown={event => {
                event.preventDefault()
                event.currentTarget.focus()
                dragOffset.current = (event.target as HTMLElement).hasAttribute('data-scroll-thumb')
                    ? event.clientY - event.currentTarget.getBoundingClientRect().top - thumbTop : thumbHeight / 2
                event.currentTarget.setPointerCapture(event.pointerId)
                move(event)
            }}
            onPointerMove={move}
            onPointerUp={event => { dragOffset.current = null; event.currentTarget.releasePointerCapture(event.pointerId) }}
            onLostPointerCapture={() => { dragOffset.current = null }}
            onKeyDown={event => {
                const element = viewport.current
                if (!element) return
                const positions: Record<string, number> = {ArrowDown: element.scrollTop + 40, ArrowUp: element.scrollTop - 40,
                    PageDown: element.scrollTop + element.clientHeight, PageUp: element.scrollTop - element.clientHeight, Home: 0, End: max}
                if (event.key in positions) { event.preventDefault(); element.scrollTop = positions[event.key] }
            }}>
            <div data-scroll-thumb className={styles.thumb} style={{height: thumbHeight, transform: `translateY(${thumbTop}px)`}}/>
        </div> : null}
    </div>
}
